import { getKeys, groupBy, sortAscending, toDict } from "swiipe.portal.shared"
import { ETranslationChangeType, ITranslationChange } from "../store/reducers/translationSlice"
import { ITranslationFileContent, ITranslationFileContentLevel } from "../type/translations/ITranslationFileContent"
import { ETranslationServerFileType, ITranslationFileDto } from "../type/translations/ITranslationFileDto"
import { ITranslationItem } from "../type/translations/ITranslationItem"
import { ITranslationRowDto } from "../type/translations/ITranslationRowDto"
import { ITranslationTagDto } from "../type/translations/ITranslationTagDto"
import { getEntries } from "../util/objectUtils"

export function getBaseTranslationLanguage() {
    return "en-US"
}

export function getKeysFromFiles(fileContent: Record<string, ITranslationFileContent>): string[] {
    return Object.keys(fileContent).reduce((acc, filePath) => {
        const content = fileContent[filePath]
        if (typeof content === "object") {
            const keys = getKeysFromJsonObject(content, "")
            acc.push(...keys.filter((k) => !acc.includes(k)))
        }
        return acc
    }, [] as string[])
}

function getKeysFromJsonObject(obj: ITranslationFileContent, keyPrefix: string): string[] {
    return Object.keys(obj).reduce((acc, key) => {
        const value = obj[key]
        if (typeof value === "string") {
            acc.push(keyPrefix + key)
        } else if (typeof value === "object") {
            acc.push(...getKeysFromJsonObject(value, keyPrefix + key + "."))
        }
        return acc
    }, [] as string[])
}

function getLevelsForRowKey(key: string): string[] {
    if (!key.includes(".") || key.endsWith(".")) {
        return [key]
    }
    return key.split(".")
}

function getLevelsForFileKey(key: string): string[] {
    if (key.includes("|")) {
        return key.split("|")
    }
    if (!key.includes(".") || key.includes(" ") || key.endsWith(".")) {
        return [key]
    }
    return key.split(".")
}

function getValueForKey(content: ITranslationFileContent, key: string): string | undefined {
    const keyLevels = getLevelsForFileKey(key)
    let current: string | ITranslationFileContentLevel = content
    for (const k of keyLevels) {
        if (current && typeof current === "object" && k in current) {
            current = current[k]
        } else {
            return undefined
        }
    }
    return typeof current === "string" ? current : undefined
}

export function parseTranslationFileContent(file: ITranslationFileDto): ITranslationFileContent {
    if (file.fileType === "Json") {
        return JSON.parse(file.content)
    } else if (file.fileType === "CsvMagento") {
        const rows = parseCsvMagentoString(file.content)
        return toDict(
            rows,
            (row) => row.key,
            (row) => row.value
        )
    }
    return {}
}

export function stringifyTranslationFileContent(file: ITranslationFileDto, parsed: ITranslationFileContent): string {
    if (file.fileType === "Json") {
        return JSON.stringify(parsed, null, 2)
    }
    if (file.fileType === "CsvMagento") {
        return getEntries(parsed)
            .map((e) => `"${e.key}", "${e.value}",\n`)
            .join("")
    }
    return ""
}

export function orderTranslationFileContentAsBaseFile(
    base: ITranslationFileContent,
    toOrder: ITranslationFileContent,
    fileType: ETranslationServerFileType
): ITranslationFileContent {
    if (fileType === "Json" || fileType === "CsvMagento") {
        return orderJsonStructure(base, toOrder)
    }
    return toOrder
}

function orderJsonStructure(base: ITranslationFileContent, toOrder: ITranslationFileContent): ITranslationFileContent {
    const baseKeys = getKeys(base)
    const toOrderKeys = sortAscending(getKeys(toOrder), (k) => {
        const index = baseKeys.indexOf(k)
        return index === -1 ? 999999 : index
    })
    return toOrderKeys.reduce((o, k) => {
        const val = toOrder[k]
        if (typeof val === "string") {
            o[k] = val
        } else {
            const compareObject = base[k]
            o[k] = orderJsonStructure(typeof compareObject !== "object" ? {} : compareObject, val)
        }
        return o
    }, {} as ITranslationFileContent)
}

export function getDefaultTranslationContentForFileType(fileType: ETranslationServerFileType): string {
    if (fileType === "Json") {
        return "{}"
    }
    if (fileType === "CsvMagento") {
        return ""
    }
    return ""
}

export function applyChangeInTranslationFile(file: ITranslationFileContent, change: ITranslationChange) {
    const levels = getLevelsForFileKey(change.key)
    if (levels.length === 0 || levels[0].trim() === "") {
        return
    }
    let fileLevel: ITranslationFileContentLevel = file
    for (let l = 0; l < levels.length; l++) {
        let nextLevel = fileLevel[levels[l]]
        if (l === levels.length - 1) {
            if (typeof nextLevel === "object") {
                throw `File ${change.group} for ${change.language} sets value on incorrect level`
            }
            fileLevel[levels[l]] = change.value
            return
        }
        if (!nextLevel) {
            fileLevel[levels[l]] = {}
            nextLevel = fileLevel[levels[l]]
        }
        if (typeof nextLevel === "string") {
            throw `File ${change.group} for ${change.language} sets value on incorrect level`
        }
        fileLevel = nextLevel
    }
}

export function mapFilesToTranslationItems(files: ITranslationFileDto[]): ITranslationItem[] {
    const filesPerGroup = groupBy(files, (f) => f.group)
    const allItems = getKeys(filesPerGroup).flatMap((groupName) => {
        const filesInGroup = filesPerGroup[groupName]
        const contentPerFile = filesInGroup.reduce((acc, file) => {
            acc[file.group + "_" + file.language] = parseTranslationFileContent(file)
            return acc
        }, {} as Record<string, ITranslationFileContent>)

        const items = mapFilesAndKeysToTranslationItemsForGroup(contentPerFile, filesInGroup, getBaseTranslationLanguage())
        return items
    })
    return allItems
}

export function mapRowsToTranslationItems(rows: ITranslationRowDto[]): ITranslationItem[] {
    const itemsGrouped = groupBy(rows, (r) => getTranslationItemId(r.group, r.key))
    const baseLanguage = getBaseTranslationLanguage()
    return getEntries(itemsGrouped).map((entry) => {
        const rows = entry.value
        const firstRow = rows[0]
        const levels = firstRow.isFlat ? [firstRow.key] : getLevelsForRowKey(firstRow.key)
        const translations = rows.map((r) => ({ language: r.language, content: r.translation }))
        return {
            base: rows.find((r) => r.language === baseLanguage)?.translation ?? "",
            group: firstRow.group,
            id: entry.key + "",
            key: firstRow.key,
            lowercaseKey: firstRow.key.toLowerCase(),
            label: firstRow.label,
            levels: levels,
            levelsString: levels.join("."),
            translations,
            lowercaseTranslations: translations.map((t) => ({ ...t, content: t.content.toLowerCase() })),
            sourceType: "row",
        } satisfies ITranslationItem
    })
}

export function getTranslationItemId(group: string, key: string) {
    return `${group}.${key}`
}

function mapFilesAndKeysToTranslationItemsForGroup(
    fileContent: Record<string, ITranslationFileContent>,
    fileDtos: ITranslationFileDto[],
    baseLanguage: string
): ITranslationItem[] {
    const allKeys = getKeysFromFiles(fileContent)
    return allKeys.map((key) => {
        const translations: { language: string; content: string }[] = []
        for (const file of fileDtos) {
            const content = fileContent[file.group + "_" + file.language]
            const value = getValueForKey(content, key)
            if (value !== undefined) {
                translations.push({ language: file.language, content: value })
            }
        }
        const levels = getLevelsForFileKey(key)
        return {
            key,
            lowercaseKey: key.toLowerCase(),
            id: getTranslationItemId(fileDtos[0].group, key),
            base: translations.find((t) => t.language === baseLanguage)?.content || "",
            translations,
            lowercaseTranslations: translations.map((t) => ({ ...t, content: t.content.toLowerCase() })),
            group: fileDtos[0].group,
            label: fileDtos[0].label,
            levels: levels,
            levelsString: levels.join("."),
            sourceType: "file",
        } satisfies ITranslationItem
    })
}

export function calculateTranslationValue(item: ITranslationItem | undefined, changes: ITranslationChange[], language: string) {
    if (!item) {
        return ""
    }
    const change = changes.find((c) => c.itemId === item.id && c.language === language)
    return (change ? change.value : item.translations.find((t) => t.language === language)?.content) ?? ""
}

export function calculateTagNamesForItems(
    items: ITranslationItem[],
    tags: ITranslationTagDto[]
): { [group: string]: { [key: string]: string[] } } {
    const groups = groupBy(items, (i) => i.group)
    const groupNames = getKeys(groups)
    const result = toDict(
        groupNames,
        (groupName) => groupName,
        (groupName) => {
            const itemsInGroup = groups[groupName]
            const tagsForGroup = tags.filter((t) => t.group === groupName)
            return toDict(
                itemsInGroup,
                (item) => item.key,
                (item) => {
                    return tagsForGroup
                        .filter(
                            (t) =>
                                (!t.key || item.key === t.key) &&
                                (!t.subKey || (item.key.startsWith(t.subKey) && item.key[t.subKey.length] === "."))
                        )
                        .map((t) => t.name)
                }
            )
        }
    )
    return result
}

function parseCsvMagentoString(str: string) {
    const lines = str.split(/,?\r?\n/)
    return lines.map((l) => {
        const m = l.match(/"(.*)",\s*"(.*)"/)
        return { key: m?.[1] ?? "", value: m?.[2] ?? "" }
    })
}

export function getLabelForTranslationChangeType(type: ETranslationChangeType) {
    switch (type) {
        case "Import":
            return "Import"
        case "PendingUpload":
            return "Pending upload"
        case "TranslationService":
            return "DeepL"
        case "UserChange":
            return "Manual change"
    }
    throw "Change type not implemented"
}
